import { type FormEvent, useEffect, useState } from "react";
import { useParams } from "react-router";

import {
  type IntakeState,
  confirmReady,
  getIntake,
  message,
  openIntake,
  saveAnswer,
} from "./intakeClient";

function text(form: FormData, name: string): string {
  return String(form.get(name) ?? "").trim();
}

function list(form: FormData, name: string): string[] {
  return text(form, name)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

const LABELS: Record<string, string> = {
  source_population: "Source population",
  rights: "Rights",
  environment_profile: "Environment profile",
  destination: "Destination",
};

/** Answers the intake checklist. The database refuses a run until it is confirmed ready. */
export function IntakeWizardPage() {
  const { engagementId = "" } = useParams();
  const [intake, setIntake] = useState<IntakeState | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    getIntake(engagementId)
      .then((found) => {
        if (live) setIntake(found);
      })
      .catch((caught: unknown) => {
        if (live) setError(message(caught));
      });
    return () => {
      live = false;
    };
  }, [engagementId]);

  function apply(work: Promise<IntakeState>) {
    work
      .then((found) => {
        setIntake(found);
        setError(null);
      })
      .catch((caught: unknown) => setError(message(caught)));
  }

  function submit(path: string, body: (form: FormData) => Record<string, unknown>) {
    return (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const form = new FormData(event.currentTarget);
      apply(saveAnswer(engagementId, path, { ...body(form), reason: text(form, "reason") }));
    };
  }

  const reasonField = (
    <label>
      Why <input name="reason" required />
    </label>
  );

  if (!intake) {
    return error ? <p role="alert">Could not load intake: {error}</p> : <p>Loading intake.</p>;
  }
  if (!intake.opened) {
    return (
      <section aria-labelledby="intake-title">
        <h1 id="intake-title">Intake checklist</h1>
        {error && <p role="alert">{error}</p>}
        <p>Opening the checklist stops runs for this engagement until it is confirmed ready.</p>
        <form
          aria-label="Open checklist"
          onSubmit={(event) => {
            event.preventDefault();
            apply(openIntake(engagementId, text(new FormData(event.currentTarget), "reason")));
          }}
        >
          {reasonField}
          <button type="submit">Open the checklist</button>
        </form>
      </section>
    );
  }

  const answered = (item: string) => item in intake.answers;
  return (
    <section aria-labelledby="intake-title">
      <h1 id="intake-title">Intake checklist</h1>
      {error && <p role="alert">{error}</p>}
      <ul aria-label="Checklist">
        {Object.entries(LABELS).map(([item, label]) => (
          <li key={item}>
            {label}: {answered(item) ? "answered" : "needed"}
          </li>
        ))}
      </ul>
      <p role="status">
        {intake.ready
          ? "Ready: a run can start."
          : "Not ready: a run cannot start until this is confirmed."}
      </p>

      <form
        aria-label="Source population"
        onSubmit={submit("/source-population", (form) => ({
          population: {
            statement: text(form, "statement"),
            bounds: { collections: list(form, "collections") },
          },
        }))}
      >
        <h2>Source population</h2>
        <label>
          What is in scope <input name="statement" required />
        </label>
        <label>
          Collections (comma separated) <input name="collections" required />
        </label>
        {reasonField}
        <button type="submit">Save source population</button>
      </form>

      <form
        aria-label="Rights"
        onSubmit={submit("/rights", (form) => ({
          rights: {
            access_classification: text(form, "classification"),
            permitted_processing_locations: list(form, "locations"),
            provider_exposure: text(form, "exposure"),
            permitted_providers: list(form, "providers"),
            verbatim_output_allowed: form.get("verbatim") === "on",
            rag_corpus_allowed: form.get("rag") === "on",
          },
        }))}
      >
        <h2>Rights</h2>
        <label>
          Classification{" "}
          <select name="classification" defaultValue="internal">
            <option value="public">public</option>
            <option value="internal">internal</option>
            <option value="confidential">confidential</option>
            <option value="restricted">restricted</option>
          </select>
        </label>
        <label>
          Permitted locations (comma separated) <input name="locations" required />
        </label>
        <label>
          Provider exposure{" "}
          <select name="exposure" defaultValue="none">
            <option value="none">none</option>
            <option value="policy_permitted">policy permitted</option>
            <option value="listed_providers">listed providers</option>
          </select>
        </label>
        <label>
          Listed providers (comma separated) <input name="providers" />
        </label>
        <label>
          <input type="checkbox" name="verbatim" /> Verbatim output allowed
        </label>
        <label>
          <input type="checkbox" name="rag" /> May enter the RAG corpus
        </label>
        {reasonField}
        <button type="submit">Save rights</button>
      </form>

      <form
        aria-label="Environment profile"
        onSubmit={submit("/environment-profile", (form) => ({
          environment_profile_id: text(form, "profile"),
        }))}
      >
        <h2>Environment profile</h2>
        <label>
          Profile id <input name="profile" required />
        </label>
        {reasonField}
        <button type="submit">Save environment profile</button>
      </form>

      <form
        aria-label="Destination"
        onSubmit={submit("/destination", (form) => ({
          destination: {
            name: text(form, "name"),
            target_objects: list(form, "objects"),
            key_fields: list(form, "keys"),
            load_expectations: text(form, "expectations"),
          },
        }))}
      >
        <h2>Destination</h2>
        <label>
          Contract name <input name="name" required />
        </label>
        <label>
          Target Maximo objects (comma separated) <input name="objects" required />
        </label>
        <label>
          Key fields (comma separated) <input name="keys" required />
        </label>
        <label>
          Load expectations <input name="expectations" required />
        </label>
        {reasonField}
        <button type="submit">Save destination</button>
      </form>

      <form
        aria-label="Confirm ready"
        onSubmit={(event) => {
          event.preventDefault();
          apply(confirmReady(engagementId, text(new FormData(event.currentTarget), "reason")));
        }}
      >
        <h2>Confirm</h2>
        {intake.missing.length > 0 && <p>Still needed: {intake.missing.join(", ")}</p>}
        {reasonField}
        <button type="submit" disabled={intake.missing.length > 0 || intake.ready}>
          Confirm ready
        </button>
      </form>
    </section>
  );
}
